import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext.js";
import { api, ApiError } from "../../api/client.js";
import type { PersonaSummary } from "../../api/types.js";
import {
  CANONICAL_COMPLETE_REFERENCE_SET,
  PERSONA_PHOTA_PREPARATION_PERIOD_MS,
  RECOMMENDED_REFERENCE_VIEWS,
  REQUIRED_TRAINING_IMAGE_COUNT_LABEL,
} from "./persona-onboarding-constants.js";

/** FIX-006 (Real Progress UX, 2026-09-13): `mm:ss`, floored and clamped at zero — never negative, e.g. from clock skew between client and server timestamps. */
function formatDurationClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

const PHOTA_READINESS_STATE_LABEL: Readonly<Record<string, string>> = {
  collecting_references: "Collecting references",
  training: "Training",
  preparing: "Preparing…",
  ready: "Ready",
  training_failed: "Training failed",
};

/**
 * Persona onboarding UX hardening (2026-09-12). The separate Reference
 * Upload screen (step 2, after Create Persona). Fetches with
 * `includeInactive=true` (a freshly-created persona is `active: false`
 * until its reference count crosses a small backend activation
 * threshold — unrelated to video-readiness, which requires the full
 * 5-image set plus Training and Preparing — and this is the one
 * surface that must show it anyway — see the backend route's own doc
 * comment for the original incident this fixes).
 *
 * FIX-007 (2026-09-13): removed the stale `MINIMUM_REFERENCE_IMAGES = 2`
 * "technically ready for video" claim below — video-readiness has
 * always actually required the full 5-image set, then Training, then
 * clearing the ~15-minute Preparing period (`photaReadinessState ===
 * "ready"`, the same condition Generate itself checks), never merely
 * "2 photos uploaded."
 *
 * UX correction (2026-09-12, round 2): `hasPhotaProfile` flips to `true`
 * the instant training is SUBMITTED (a `persona_phota_profiles` row
 * exists with a non-null `phota_profile_id` well before `status` reaches
 * `READY`) — confirmed against a real live persona-creation run.
 * Filtering the CURRENTLY SELECTED persona out the moment that happens
 * would yank the whole card off-screen right as training starts,
 * before the user ever sees "Training" or "Ready." So there are
 * deliberately two different views of the persona list here:
 * `selectablePersonas` (offered in the dropdown for a NEW selection —
 * excludes already-complete personas, unchanged) vs. `selectedPersona`
 * (looked up in the FULL, unfiltered list) — once chosen, a persona
 * stays visible through its entire uploading -> training -> ready
 * arc, even after it's no longer offered for a fresh pick. Periodic
 * refetch (every 5s) is what actually drives the automatic
 * uploading -> training -> ready transition on screen.
 */
export function PersonaReferenceUploadTab({ tenantId }: { readonly tenantId: string }) {
  const { user, csrfToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [personas, setPersonas] = useState<PersonaSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(searchParams.get("persona"));
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // FIX-006 (Real Progress UX, 2026-09-13): a lightweight LOCAL display
  // timer only — it never calls the server and never changes
  // `photaReadinessState`. It exists purely so the Training elapsed
  // clock / Preparing countdown tick up/down on screen every second
  // without waiting for the next 5s poll. The existing 5s
  // `loadPersonas` poll below remains the sole source of truth for any
  // actual state transition.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const loadPersonas = useCallback(async () => {
    try {
      const result = await api.get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas?includeInactive=true`);
      setPersonas(result.personas);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : "Could not load your personas — please try again.");
    }
  }, [tenantId]);

  useEffect(() => {
    void loadPersonas();
  }, [loadPersonas]);

  // Drives the automatic uploading -> training -> ready transition on
  // screen without a manual refresh.
  useEffect(() => {
    const interval = setInterval(() => {
      void loadPersonas();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadPersonas]);

  const allPersonas = personas ?? [];
  // Offered for a NEW selection only — already-complete personas are excluded here, unchanged.
  const selectablePersonas = allPersonas.filter((p) => p.hasPhotaProfile !== true || p.personaId === selectedPersonaId);
  // The selected persona's own data always comes from the FULL list, so it
  // never disappears mid-flow once chosen.
  const selectedPersona = allPersonas.find((p) => p.personaId === selectedPersonaId) ?? null;

  useEffect(() => {
    if (selectedPersonaId !== null) {
      return;
    }
    const queryPersonaId = searchParams.get("persona");
    if (queryPersonaId !== null && allPersonas.some((p) => p.personaId === queryPersonaId)) {
      setSelectedPersonaId(queryPersonaId);
    }
  }, [allPersonas, searchParams, selectedPersonaId]);

  function handleSelectPersona(personaId: string): void {
    setSelectedPersonaId(personaId);
    setSearchParams({ persona: personaId }, { replace: true });
  }

  async function handleUploadImages(files: FileList | null): Promise<void> {
    if (files === null || files.length === 0 || user === null || selectedPersona === null) {
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.type !== "image/jpeg" && file.type !== "image/png") {
          setUploadError(`${file.name}: only JPEG or PNG images are supported.`);
          continue;
        }
        const params = new URLSearchParams({ contentType: file.type, originalFilename: file.name });
        await api.uploadBinary(`/videos/${tenantId}/personas/${encodeURIComponent(selectedPersona.personaId)}/reference-images?${params.toString()}`, csrfToken, file);
      }
      await loadPersonas();
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : "Could not upload one or more images — please try again.");
    } finally {
      setUploading(false);
    }
  }

  if (personas === null && loadError === null) {
    return <div className="empty-state">Loading…</div>;
  }
  if (loadError !== null) {
    return <div className="error-banner">{loadError}</div>;
  }

  // Real, persisted count from the server — never local optimistic state.
  const activeReferenceCount = selectedPersona?.activeReferenceCount ?? 0;
  const atCap = activeReferenceCount >= CANONICAL_COMPLETE_REFERENCE_SET;
  // The SAME readiness condition the Generate flow itself uses (CreateTab/OnboardingGate) — never a separate/looser definition.
  const isReady = selectedPersona?.photaReadinessState === "ready";
  // Once 50/50 is reached OR a Phota Profile already exists (training submitted/in progress/failed), uploading is closed — belt-and-suspenders with the server's own PersonaAlreadyHasPhotaProfileError guard.
  const uploadClosed = atCap || selectedPersona?.hasPhotaProfile === true;
  const progressPercent = Math.min(100, Math.round((activeReferenceCount / CANONICAL_COMPLETE_REFERENCE_SET) * 100));

  // FIX-006 (Real Progress UX, 2026-09-13): real elapsed Training time,
  // computed from the CURRENT Phota Profile's persisted `createdAt` —
  // never from when this page happened to be opened. `null` when the
  // server hasn't provided the timestamp (e.g. an older response shape,
  // or no current attempt), in which case the generic fallback text is
  // shown instead of a fabricated timer.
  const trainingElapsedMs =
    selectedPersona?.photaTrainingStartedAt != null ? Math.max(0, now - new Date(selectedPersona.photaTrainingStartedAt).getTime()) : null;

  // Real remaining-time Preparing countdown + progress, derived ONLY
  // from `now - readyAt` against the existing fixed 15-minute
  // preparation window — never an invented/estimated duration. Reaching
  // 00:00 here does NOT mean Ready: `photaReadinessState` (server-driven,
  // refreshed by the existing 5s poll) is the only thing that can ever
  // flip the READY indicator below.
  const preparingReadyAtMs = selectedPersona?.photaProfileReadyAt != null ? new Date(selectedPersona.photaProfileReadyAt).getTime() : null;
  const preparingRemainingMs = preparingReadyAtMs !== null ? Math.max(0, preparingReadyAtMs + PERSONA_PHOTA_PREPARATION_PERIOD_MS - now) : null;
  const preparingPercent =
    preparingReadyAtMs !== null
      ? Math.min(100, Math.max(0, Math.round(((now - preparingReadyAtMs) / PERSONA_PHOTA_PREPARATION_PERIOD_MS) * 100)))
      : null;

  function progressStatusText(): string {
    if (isReady) {
      return `Complete reference set uploaded (${CANONICAL_COMPLETE_REFERENCE_SET} / ${CANONICAL_COMPLETE_REFERENCE_SET}).`;
    }
    if (selectedPersona?.photaReadinessState === "training") {
      return trainingElapsedMs !== null
        ? `Training — ${formatDurationClock(trainingElapsedMs)} elapsed`
        : "Training in progress — this model is being trained automatically. This can take a few minutes.";
    }
    if (selectedPersona?.photaReadinessState === "preparing") {
      return preparingRemainingMs !== null
        ? `Preparing — ${formatDurationClock(preparingRemainingMs)} remaining`
        : "Training is complete. Your Persona is now Preparing — this takes about 15 minutes before it's Ready.";
    }
    return "Training in progress — this model is being trained automatically. This can take a few minutes.";
  }

  return (
    <div>
      <h2>Upload reference photos</h2>
      <div className="card" style={{ maxWidth: 480 }}>
        <p className="muted">
          Upload {REQUIRED_TRAINING_IMAGE_COUNT_LABEL} clear photos of the same person. For the best result, use five different useful views — five
          good, varied photos work better than many similar ones. You choose which photos to upload — VIDEO STUDIO does not classify, tag, or sort
          them.
        </p>
        <ul className="muted" style={{ paddingLeft: 20, lineHeight: 1.6 }}>
          {RECOMMENDED_REFERENCE_VIEWS.map((view) => (
            <li key={view.label}>
              <strong>{view.label}</strong> — {view.description}
            </li>
          ))}
        </ul>
        <p className="muted">
          Use clear, well-lit photos with the face visible. Avoid sunglasses, heavy filters, extreme shadows, blurry photos, or anything covering the
          face.
        </p>
        <p className="muted">
          Training starts automatically once all {CANONICAL_COMPLETE_REFERENCE_SET} reference photos are uploaded — there is no separate "Start
          Training" step. After training finishes, the Persona enters Preparing for about 15 minutes. Only once it reaches Ready is it available
          for normal video creation.
        </p>

        <div className="field">
          <label htmlFor="persona-select">Persona</label>
          {selectablePersonas.length === 0 ? (
            <p className="muted">No personas need reference photos right now — every persona already has a complete model.</p>
          ) : (
            <select id="persona-select" value={selectedPersonaId ?? ""} onChange={(event) => handleSelectPersona(event.target.value)}>
              <option value="" disabled>
                Select a persona…
              </option>
              {selectablePersonas.map((persona) => (
                <option key={persona.personaId} value={persona.personaId}>
                  {persona.displayName}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedPersona && (
          <>
            <h3>
              {selectedPersona.displayName} — {activeReferenceCount} / {CANONICAL_COMPLETE_REFERENCE_SET} reference photos
              {selectedPersona.photaReadinessState !== null && selectedPersona.photaReadinessState !== undefined && (
                <span className="badge status-ready_for_review" style={{ marginLeft: 6 }}>
                  {PHOTA_READINESS_STATE_LABEL[selectedPersona.photaReadinessState] ?? selectedPersona.photaReadinessState}
                </span>
              )}
            </h3>

            <div
              className="reference-upload-progress-track"
              role="progressbar"
              aria-valuenow={activeReferenceCount}
              aria-valuemin={0}
              aria-valuemax={CANONICAL_COMPLETE_REFERENCE_SET}
              aria-label="Reference photos uploaded"
            >
              <div className="reference-upload-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>

            {/*
             * A persistent completion indicator, present through the whole
             * flow — inactive/muted until the persona reaches the SAME
             * readiness condition Generate itself uses, then switches to a
             * highlighted "lit" state. Deliberately a <span role="status">,
             * never a real <button>: it performs no action on click (the
             * user explicitly confirmed a pure visual indicator, no
             * navigation), so a real button would falsely imply an
             * interactive affordance.
             */}
            <span
              className={isReady ? "ready-indicator-lit" : "ready-indicator-inactive"}
              role="status"
              aria-label={isReady ? "Ready" : "Not ready yet"}
            >
              READY
            </span>

            {uploadError && <div className="error-banner">{uploadError}</div>}
            {uploadClosed ? (
              <>
                <p className="muted" aria-live="polite">
                  {progressStatusText()}
                </p>
                {/*
                 * FIX-006 (Real Progress UX, 2026-09-13): Preparing's own
                 * progress bar — a real fixed-duration window, so (unlike
                 * Training, which has no deterministic length and
                 * therefore shows only an elapsed clock, never a
                 * percentage) a percentage is truthful here. Reusing the
                 * same generic track/fill look as the reference-photo bar
                 * above — a shared visual style, not the same meaning.
                 */}
                {!isReady && selectedPersona.photaReadinessState === "preparing" && preparingPercent !== null && (
                  <div
                    className="reference-upload-progress-track"
                    role="progressbar"
                    aria-valuenow={preparingPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Preparing progress"
                  >
                    <div className="reference-upload-progress-fill" style={{ width: `${preparingPercent}%` }} />
                  </div>
                )}
              </>
            ) : (
              <div className="field">
                <label htmlFor="upload-references">Add reference photos (JPEG/PNG)</label>
                <input
                  id="upload-references"
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  disabled={uploading}
                  onChange={(event) => void handleUploadImages(event.target.files)}
                />
              </div>
            )}
            {uploading && <p className="muted">Uploading…</p>}
          </>
        )}
      </div>
    </div>
  );
}
